import {
	createStyles,
	Divider,
	Menu,
	Stack,
	type MantineStyleSystemProps,
	type MenuProps,
} from '@mantine/core';
import type { FloatingPosition } from '@mantine/core/lib/Floating';
import { keys, noop } from '@mantine/utils';
import type { ButtonVariants, IconNames } from '@repo/foundations';
import { Icon, Text } from '@repo/foundations';
import {
	every,
	filter,
	forEach,
	groupBy,
	includes,
	isEmpty,
	isNil,
	map,
	partition,
	size,
	sortBy,
	startCase,
	uniq,
} from 'lodash-es';
import type React from 'react';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { Virtuoso } from 'react-virtuoso';
import { singularize } from '../../utils';
import type { ItemIconType } from '../ItemIcon';
import RichTooltip from '../RichTooltip/RichTooltip';
import type { SelectablePropertyType } from '../SelectableProperty/types';
import type { SelectablePropertyItem } from '../SingleSelector/types';
import { DEFAULT_ITEM_SIZE } from './constants';
import type { IMultiSelectorItemProps } from './MultiSelectorItem';
import MultiSelectorItem from './MultiSelectorItem';
import MultiSelectorTarget from './MultiSelectorTarget';
import { createMultiSelectorTargetProps } from './multiSelectorUtils';
import SelectorSearch from './SelectorSearch';

type UnifiedListItem =
	| { type: 'item'; item: SelectablePropertyItem; isSelected: boolean }
	| { type: 'divider' }
	| { type: 'groupLabel'; group: string };

export interface IMultiSelectorProps extends MantineStyleSystemProps {
	placeholder?: string | null;
	placeholderIconName?: IconNames;
	searchPlaceholder?: string;
	variant?: ButtonVariants;
	/**
	 * The initially selected values.
	 */
	initialSelected: string[] | boolean[];
	/**
	 * The type of selectable property.
	 */
	property: SelectablePropertyType;
	/**
	 * The inherited values (e.g., entity's teams inherited from integrations).
	 * These values are shown but not editable.
	 */
	inheritedValues?: string[];
	/**
	 * The tooltip text for inherited values.
	 */
	inheritedValuesTooltip?: string | Record<string, string>;
	/**
	 * The icon type for the property.
	 */
	iconType: ItemIconType;
	/**
	 * The options for selection.
	 */
	options: SelectablePropertyItem[];
	/**
	 * The permitted ID for selection.
	 */
	permittedId: string;
	/**
	 * Determines if the menu items should display a badge.
	 */
	isMenuItemBadge: boolean;
	/**
	 * Determines if the viewer is a user.
	 */
	isViewerUser: boolean;
	/**
	 * The function called when the selection changes.
	 */
	onChange?: (newValue: (string | boolean)[]) => void;
	/**
	 * Function to validate if a selection change should be allowed.
	 * Return false to prevent the selection change.
	 */
	onValidateSelection?: (
		item: SelectablePropertyItem,
		currentSelection: SelectablePropertyItem[]
	) => boolean;
	/**
	 * Determines if the selector is read only.
	 */
	readOnly?: boolean;
	/**
	 * The component to render for each item.
	 */
	selectorItem?: React.FunctionComponent<IMultiSelectorItemProps>;
	/**
	 * The size of each item.
	 */
	itemSize?: number;
	/**
	 *  The number of items to render.
	 */
	itemsToRender?: number;
	hideOnEmpty?: boolean;
	propertyLabel?: string;
	/**
	 * The function called when the search term changes.
	 */
	onSearchTermChange?: (value: string) => void;
	emptyState?: React.ReactNode;
	onCreate?: (initTitle: string) => void;
	withDescriptionTooltip?: boolean;
	/**
	 * Determines if the component is within a portal.
	 * Needs to be set to false for chrome extension to have
	 * proper styling
	 */
	withinPortal?: boolean;
	/**
	 * custome virtuoso height used when selector is inside of
	 * a modal, portal, or etc
	 */
	virtuosoHeight?: number | string;

	/**
	 * Custom menu handling for when portal is interfering with
	 * default event handling of mantine menu
	 * Required for chrome extension
	 */
	opened?: boolean;
	onTargetClick?: () => void;
	target?: React.ReactElement;
	isChromeUser?: boolean;
	className?: string;
	classNames?: MenuProps['classNames'];
	/**
	 * Determines if the inherited values should be returned on empty. If false an empty array will be returned.
	 */
	returnInheritedValuesOnEmpty?: boolean;
	/**
	 * If set to true, the dropdown will not have a max height
	 * and will scroll indefinitely.
	 */
	noMaxHeightDropdown?: boolean;
	position?: FloatingPosition;
	/**
	 * If set to true, the dropdown will be opened by default
	 */
	defaultOpened?: boolean;
}

const useStyles = createStyles(
	(
		theme,
		{ itemSize, itemsToRender }: { itemSize: number; itemsToRender: number }
	) => ({
		selectedStack: {
			maxHeight: itemSize * itemsToRender,
			overflowY: 'auto',
		},
		createTagText: {
			color: theme.other.getColor('text/secondary/default'),
		},
	})
);

/**
 * Renders a multi-selector component.
 * @param placeholder - Placeholder text for the selector.
 * @param placeholderIconName - Icon name for the placeholder.
 * @param variant - Variant of the selector.
 * @param initialSelected - Array of initially selected items.
 * @param inheritedValues - Array of inherited values.
 * @param inheritedValuesTooltip - Tooltip for inherited values.
 * @param property - Property of the selector.
 * @param iconType - Icon for the property.
 * @param options - Available options for selection.
 * @param permittedId - Permitted ID for selection.
 * @param isMenuItemBadge - Boolean indicating if the item is a menu item badge.
 * @param isViewerUser - Boolean indicating if the user is a viewer.
 * @param onChange - Callback function when the selection changes.
 * @param readOnly - Boolean indicating if the selector is read only.
 * @param selectorItem - Component to render for each item.
 * @param itemSize - Size of each item.
 * @param itemsToRender - Number of items to render.
 * @param onSearchTermChange - Callback function when the search term changes.
 * @param onCreate - Callback function when a new item is created.
 */
function MultiSelector({
	placeholder = 'Empty',
	searchPlaceholder = 'Search...',
	placeholderIconName,
	variant,
	initialSelected,
	inheritedValues = [],
	inheritedValuesTooltip,
	property,
	propertyLabel,
	iconType,
	options,
	permittedId,
	isMenuItemBadge,
	hideOnEmpty,
	isViewerUser,
	onChange,
	onValidateSelection,
	readOnly,
	selectorItem: SelectorItem = MultiSelectorItem,
	itemSize = DEFAULT_ITEM_SIZE,
	itemsToRender = 8,
	onSearchTermChange,
	emptyState,
	onCreate,
	w,
	className,
	withDescriptionTooltip = false,
	withinPortal = true,
	classNames,
	virtuosoHeight = 256,
	opened,
	target,
	onTargetClick,
	isChromeUser = false,
	returnInheritedValuesOnEmpty = true,
	noMaxHeightDropdown = false,
	position = 'bottom-start',
	defaultOpened = false,
}: IMultiSelectorProps) {
	// State variables
	const [searchTerm, setSearchTerm] = useState('');
	const [selected, setSelected] = useState<SelectablePropertyItem[]>([]);
	const [notSelected, setNotSelected] = useState<SelectablePropertyItem[]>([]);
	const [hasGroups, setHasGroups] = useState<boolean>(false);
	const [groups, setGroups] = useState<string[]>([]);
	const [notSelectedGroups, setNotSelectedGroups] = useState<
		Record<string, SelectablePropertyItem[]>
	>({});

	const { classes, theme } = useStyles({ itemSize, itemsToRender });

	// Effect to update the selected and not selected items based on the initialSelected and options
	useEffect(() => {
		const partitionedSelected = partition(sortBy(options, 'label'), (o) =>
			initialSelected.includes(o.value as never)
		);

		setSelected(partitionedSelected[0]);

		if (!isEmpty(options)) {
			// Show groups in the not selected list
			if ('group' in options[0]) {
				setHasGroups(true);
				setGroups(uniq(map(options, 'group')) as string[]);
				setNotSelectedGroups(groupBy(partitionedSelected[1], 'group'));
			} else {
				setNotSelected(partitionedSelected[1]);
			}
		}

		if (isEmpty(options)) {
			setNotSelected([]);
		}
	}, [initialSelected, options]);

	// Filter the selected items by excluding the inherited values
	const inheritSelected: SelectablePropertyItem[] = useMemo(
		() =>
			inheritedValues
				?.map(
					(v) => options.find((o) => o.value === v) as SelectablePropertyItem
				)
				.filter((v) => !isNil(v)),
		[inheritedValues, options]
	);

	// Memoized filtered selected items based on the search term and inherited values
	const filteredSelected = useMemo(() => {
		const items = filter(
			selected,
			(item) =>
				!item.hidden && !inheritSelected.find((v) => v.value === item.value)
		);

		if (isEmpty(searchTerm)) {
			return items;
		}

		return filter(items, (item) =>
			item.label.toLowerCase().includes(searchTerm.toLowerCase())
		);
	}, [selected, searchTerm, inheritSelected]);

	// Memoized filtered not selected items based on the search term, inherited values, and user permissions
	const filteredNotSelected = useMemo(() => {
		const items = filter(
			filter(
				notSelected,
				(item) => !inheritSelected.find((v) => v.value === item.value)
			),
			(item) => !item.hidden
		);

		if (isViewerUser && size(permittedId) > 0) {
			return filter(items, (item) => item.value === permittedId);
		}

		if (isEmpty(searchTerm)) {
			return items;
		}

		return filter(
			filter(items, (item) =>
				item.label?.toLowerCase()?.includes(searchTerm.toLowerCase())
			)
		);
	}, [notSelected, isViewerUser, permittedId, searchTerm, inheritSelected]);

	// Memoized filtered not selected items grouped by groups based on the search term, inherited values, and user permissions
	const filteredNotSelectedGroups = useMemo(() => {
		const newNotSelectedGroups = { ...notSelectedGroups };

		forEach(groups, (group) => {
			newNotSelectedGroups[group] = filter(
				newNotSelectedGroups[group],
				(item) => !inheritSelected.find((v) => v.value === item.value)
			);
		});

		// Filter out the hidden items from both groups
		// Not sure if there is a workaround to make this more efficient/succinct
		groups.forEach((group) => {
			newNotSelectedGroups[group] =
				newNotSelectedGroups[group]?.filter((item) => !item.hidden) || [];
			notSelectedGroups[group] =
				notSelectedGroups[group]?.filter((item) => !item.hidden) || [];
		});

		if (isViewerUser && size(permittedId) > 0) {
			forEach(groups, (group) => {
				newNotSelectedGroups[group] = filter(
					newNotSelectedGroups[group],
					(item) => item.value === permittedId
				);
			});

			return newNotSelectedGroups;
		}

		if (isEmpty(searchTerm)) {
			return notSelectedGroups;
		}

		forEach(groups, (group) => {
			newNotSelectedGroups[group] = filter(
				newNotSelectedGroups[group],
				(item) => item.label.toLowerCase().includes(searchTerm.toLowerCase())
			);
		});

		return newNotSelectedGroups;
	}, [
		notSelectedGroups,
		groups,
		isViewerUser,
		permittedId,
		searchTerm,
		inheritSelected,
	]);

	// Check if there are selected and not selected items
	const hasSelectedAndNotSelected = useMemo(
		() =>
			!isEmpty(filteredSelected) &&
			!isEmpty(filteredNotSelected) &&
			(!isViewerUser || size(permittedId) > 0),
		[filteredSelected, filteredNotSelected, isViewerUser, permittedId]
	);

	// Check if there are no search results
	const hasNoResults = useMemo(() => {
		if (hasGroups) {
			return (
				isEmpty(options) ||
				(isEmpty(inheritedValues) &&
					isEmpty(filteredSelected) &&
					every(filteredNotSelectedGroups, (group) => isEmpty(group)))
			);
		}

		return (
			isEmpty(options) ||
			(isEmpty(inheritedValues) &&
				isEmpty(filteredSelected) &&
				isEmpty(filteredNotSelected))
		);
	}, [
		hasGroups,
		options,
		inheritedValues,
		filteredSelected,
		filteredNotSelected,
		filteredNotSelectedGroups,
	]);

	// Check if not selected items should be shown
	const showNotSelected = useMemo(
		() =>
			!isEmpty(filteredNotSelected) &&
			!hasGroups &&
			(!isViewerUser || size(permittedId) > 0),
		[filteredNotSelected, hasGroups, isViewerUser, permittedId]
	);

	const handleSearchTermChange = useCallback(
		(value: string) => {
			setSearchTerm(value);
			onSearchTermChange?.(value);
		},
		[onSearchTermChange]
	);

	// Handler when an item is clicked
	const handleOnClick = useCallback(
		(item: SelectablePropertyItem) => {
			// Check if selection is allowed before making any state changes
			if (onValidateSelection && !onValidateSelection(item, selected)) {
				return; // Don't update state if validation fails
			}

			const newSelected = includes(map(selected, 'value'), item.value)
				? filter(selected, (s) => s.value !== item.value)
				: sortBy([...selected, item], 'label');
			setSelected(newSelected);

			// Handle groups
			if (hasGroups) {
				const newNotSelectedGroups = { ...notSelectedGroups };

				groups.forEach((group) => {
					const itemInNotSelectedGroup = includes(
						map(newNotSelectedGroups[group], 'value'),
						item.value
					);

					if (itemInNotSelectedGroup) {
						// Remove from group if it exists
						newNotSelectedGroups[group] = filter(
							newNotSelectedGroups[group],
							(s) => s.value !== item.value
						);
					} else if (group === item.group) {
						// Add to group if it doesn't exist and is in the same group
						newNotSelectedGroups[group] = newNotSelectedGroups[group]
							? sortBy([...newNotSelectedGroups[group], item], 'label')
							: [item];
					}
				});
				setNotSelectedGroups(newNotSelectedGroups);
			} else {
				const newNotSelected = includes(map(notSelected, 'value'), item.value)
					? filter(notSelected, (s) => s.value !== item.value)
					: sortBy([...notSelected, item], 'label');
				setNotSelected(newNotSelected);
			}

			if (onChange) {
				if (isEmpty(newSelected)) {
					onChange(returnInheritedValuesOnEmpty ? inheritedValues : []);
				} else {
					onChange(map(newSelected, 'value'));
				}
			}

			// Handle onClick on item if it exists (e.g., for owners/groups)
			if (!isNil(item.onClick)) {
				if (hasGroups) {
					item.onClick(
						map(filter(newSelected, { group: item.group }), 'value')
					);
				} else {
					item.onClick(map(newSelected, 'value'));
				}
			}
		},
		[
			groups,
			hasGroups,
			notSelected,
			notSelectedGroups,
			onChange,
			selected,
			onValidateSelection,
			inheritedValues,
			returnInheritedValuesOnEmpty,
		]
	);

	const handleOnCreate = useCallback(() => {
		if (onCreate) {
			onCreate?.(searchTerm);
		}
	}, [onCreate, searchTerm]);

	const unifiedList = useMemo((): UnifiedListItem[] => {
		const list: UnifiedListItem[] = [];

		if (!isEmpty(filteredSelected)) {
			filteredSelected.forEach((item) => {
				list.push({ type: 'item', item, isSelected: true });
			});
		}

		if (hasSelectedAndNotSelected) {
			list.push({ type: 'divider' });
		}

		if (hasGroups && (!isViewerUser || size(permittedId) > 0)) {
			keys(filteredNotSelectedGroups).forEach((group) => {
				if (size(filteredNotSelectedGroups[group]) > 0) {
					list.push({ type: 'groupLabel', group });
					filteredNotSelectedGroups[group].forEach((item) => {
						list.push({ type: 'item', item, isSelected: false });
					});
				}
			});
		} else if (showNotSelected) {
			filteredNotSelected.forEach((item) => {
				list.push({ type: 'item', item, isSelected: false });
			});
		}

		return list;
	}, [
		filteredSelected,
		filteredNotSelected,
		filteredNotSelectedGroups,
		hasSelectedAndNotSelected,
		hasGroups,
		showNotSelected,
		isViewerUser,
		permittedId,
	]);

	const height = useMemo(
		() =>
			size(unifiedList) > itemsToRender
				? virtuosoHeight
				: size(unifiedList) * itemSize,
		[unifiedList, itemSize, itemsToRender, virtuosoHeight]
	);

	const itemContent = useCallback(
		(index: number, listItem: UnifiedListItem) => {
			if (listItem.type === 'divider') {
				return <Divider key={`divider-${index}`} my={theme.spacing['2xs']} />;
			}

			if (listItem.type === 'groupLabel') {
				return (
					<Menu.Label key={`group-${listItem.group}`}>
						{startCase(listItem.group)}
					</Menu.Label>
				);
			}

			const { item, isSelected } = listItem;
			const itemElement = (
				<SelectorItem
					key={`${item.value}`}
					iconType={iconType}
					item={item}
					isMenuItemBadge={isMenuItemBadge}
					isViewerUser={isViewerUser}
					permittedId={permittedId}
					onClick={handleOnClick}
					checked={isSelected}
				/>
			);

			if (isSelected) {
				return (
					<RichTooltip
						key={`${item.label}+${item.value}`}
						title={item.label}
						body={item.description}
						position="left"
						hidden={!withDescriptionTooltip || !item.description}
						data-testid={`multi-selector-${property}-selected-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
					>
						{itemElement}
					</RichTooltip>
				);
			}

			if (withDescriptionTooltip && item.description) {
				return (
					<RichTooltip
						key={`${item.label}+${item.value}`}
						title={item.label}
						body={item.description}
						position="left"
						hidden={false}
					>
						{itemElement}
					</RichTooltip>
				);
			}

			return itemElement;
		},
		[
			theme,
			SelectorItem,
			handleOnClick,
			iconType,
			isMenuItemBadge,
			isViewerUser,
			permittedId,
			withDescriptionTooltip,
			property,
		]
	);

	const getInheritedValueDisabledTooltip = useCallback(
		(item: SelectablePropertyItem): string | undefined => {
			if (typeof inheritedValuesTooltip === 'string') {
				return inheritedValuesTooltip;
			}

			if (
				typeof inheritedValuesTooltip === 'object' &&
				typeof item.value === 'string'
			) {
				return inheritedValuesTooltip[item.value] ?? '';
			}

			return undefined;
		},
		[inheritedValuesTooltip]
	);

	// If the user is a viewer and using chrome extension,
	// allow user to click on target to open menu but it should
	// only display the selected items
	if (isViewerUser && isChromeUser && size(selected) >= 1) {
		return (
			<Menu
				keepMounted={false}
				width={300}
				position="bottom-start"
				closeOnItemClick={false}
				withinPortal={withinPortal}
				classNames={classNames}
				opened={opened}
				defaultOpened={defaultOpened}
			>
				<Menu.Target>
					{target || (
						<MultiSelectorTarget
							hideOnEmpty={hideOnEmpty}
							placeholder={placeholder}
							placeholderIconName={placeholderIconName}
							variant={variant}
							inheritSelected={inheritSelected}
							selected={selected}
							property={property}
							propertyLabel={propertyLabel}
							iconType={iconType}
							isMenuItemBadge={isMenuItemBadge}
							isViewerUser={isViewerUser}
							permittedId={permittedId}
							readOnly={readOnly || isNil(onChange)}
							className={className}
							w={w}
							withDescriptionTooltip={withDescriptionTooltip}
							onTargetClick={onTargetClick}
							isChromeUser={isChromeUser}
						/>
					)}
				</Menu.Target>
				<Menu.Dropdown>
					{map(inheritSelected, (item) => (
						<SelectorItem
							key={`${item.label}-${item.value}`}
							disabled
							disabledTooltip={getInheritedValueDisabledTooltip(item)}
							iconType={iconType}
							item={item}
							isMenuItemBadge={isMenuItemBadge}
							isViewerUser={isViewerUser}
							permittedId={permittedId}
							onClick={noop}
							checked
							isChromeUser={isChromeUser}
						/>
					))}
					{!isEmpty(filteredSelected) && (
						<Stack className={classes.selectedStack} spacing={0}>
							{map(filteredSelected, (item) => (
								<RichTooltip
									key={`${item.label}+${item.value}`}
									title={item.label}
									body={item.description}
									position="left"
									hidden={!withDescriptionTooltip || !item.description}
								>
									<SelectorItem
										key={`${item.label}+${item.value}`}
										iconType={iconType}
										item={item}
										isMenuItemBadge={isMenuItemBadge}
										isViewerUser={isViewerUser}
										permittedId={permittedId}
										onClick={noop}
										checked
										isChromeUser={isChromeUser}
									/>
								</RichTooltip>
							))}
						</Stack>
					)}
				</Menu.Dropdown>
			</Menu>
		);
	}

	// If there is only one selected item and no permitted ID, render the target component instead
	if (
		readOnly ||
		(isViewerUser && size(selected) <= 1 && size(permittedId) === 0)
	) {
		return (
			<MultiSelectorTarget
				{...createMultiSelectorTargetProps(
					{
						hideOnEmpty,
						placeholder,
						placeholderIconName,
						inheritSelected,
						selected,
						property,
						propertyLabel,
						iconType,
						isMenuItemBadge,
						isViewerUser,
						permittedId,
						readOnly: readOnly || isNil(onChange),
						w,
						className,
						withDescriptionTooltip,
						onTargetClick,
					},
					{ variant: 'tertiary' }
				)}
			/>
		);
	}

	return (
		<Menu
			keepMounted={false}
			width={300}
			position={position}
			closeOnItemClick={false}
			withinPortal={withinPortal}
			classNames={classNames}
			opened={opened}
			defaultOpened={defaultOpened}
		>
			<Menu.Target>
				{target || (
					<MultiSelectorTarget
						data-testid={`multi-selector-${property}`}
						{...createMultiSelectorTargetProps(
							{
								hideOnEmpty,
								placeholder,
								placeholderIconName,
								inheritSelected,
								selected,
								property,
								propertyLabel,
								iconType,
								isMenuItemBadge,
								isViewerUser,
								permittedId,
								readOnly: readOnly || isNil(onChange),
								className,
								w,
								withDescriptionTooltip,
								onTargetClick,
							},
							{ variant }
						)}
					/>
				)}
			</Menu.Target>
			<Menu.Dropdown
				sx={{
					maxHeight: noMaxHeightDropdown ? undefined : '33vh',
					overflowY: 'auto',
					scrollBehavior: 'smooth',
					overflowX: 'hidden',
					scrollbarColor: `${theme.other.getColor('surface/tertiary/active')} transparent`,
				}}
			>
				<SelectorSearch
					searchTerm={searchTerm}
					setSearchTerm={handleSearchTermChange}
					placeholder={searchPlaceholder}
				/>
				{map(inheritSelected, (item) => (
					<SelectorItem
						key={`${item.label}-${item.value}`}
						disabled
						disabledTooltip={getInheritedValueDisabledTooltip(item)}
						iconType={iconType}
						item={item}
						isMenuItemBadge={isMenuItemBadge}
						isViewerUser={isViewerUser}
						permittedId={permittedId}
						onClick={noop}
						checked
					/>
				))}
				{!isEmpty(unifiedList) && (
					<Virtuoso
						data-testid={`multi-selector-${property}-virtuoso`}
						style={{
							height,
						}}
						data={unifiedList}
						totalCount={size(unifiedList)}
						itemContent={itemContent}
					/>
				)}
				{hasNoResults && emptyState}
				{onCreate && hasNoResults && (
					<>
						<Menu.Divider my={theme.spacing['2xs']} />
						<Menu.Item
							icon={<Icon name="plus" />}
							closeMenuOnClick
							onClick={handleOnCreate}
						>
							Create new {singularize(property)}
							{searchTerm && (
								<Text size="sm" className={classes.createTagText} span>
									: "{searchTerm}"
								</Text>
							)}
						</Menu.Item>
					</>
				)}
			</Menu.Dropdown>
		</Menu>
	);
}

export default memo(MultiSelector);
